// Официальный контур выплат: журнал погашения белой части зарплаты, период с 15 по 15 число.
//
// ## Почему сетка на всю ширину раздела
//
// Колонок здесь тринадцать и больше: фамилия, полная сумма, процент, удержано, к выдаче,
// основная выплата, до пяти недельных траншей, выплачено, заведено, остаток, состояние,
// заметка. В колонку детали это не помещается физически — как и табель, экран занимает всю
// ширину, а выбор периода уезжает в его собственную шапку.
//
// ## Что человек видит в первую секунду
//
// Реестр периодов уже несёт итоги, поэтому шапка и полоса итогов заполняются из него — до
// того, как приедет сетка. Сетка догружается под ними, прошлый её срез при этом остаётся на
// экране: спиннер на месте уже показанного числа запрещён (правило 11).
//
// ## Ноль нажатий до ответа
//
// Вопрос, ради которого этот экран открывают, один: «могу ли я закрывать период». Ответ стоит
// полосой проверки сразу под шапкой и приезжает в том же ответе, что и сетка. Предпросмотр
// закрытия грузится заранее, поэтому окно подтверждения открывается уже с числами.
//
// ## Правка суммы НЕ должна сбрасывать процент
//
// Загрузка строк пачкой перезаписывает строку целиком: не прислав процент, состояние и заметку,
// мы их сотрём. Поэтому правка одной ячейки собирает тело из ТЕКУЩИХ значений строки и меняет
// в нём ровно одно поле. Иначе поправленная сумма молча снимала бы удержание по исполнительному
// документу — то есть отдавала бы приставам чужие деньги обратно человеку.

import { useEffect, useRef, useState } from 'react';

import {
  RiCloseLine,
  RiFileList3Line,
  RiInboxUnarchiveLine,
  RiInformationLine,
  RiLockLine,
} from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router';

import type { OfficialPeriodOut, OfficialRowOut, RowIn } from '@/shared/api/endpoints/official';
import { useAuthStore } from '@/shared/auth/store';
import { DataGrid, type PastePlan } from '@/shared/grid';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, pushPanel, type SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDataAsOf } from '@/shared/time/format';
import { formatPeriodTitle } from '@/shared/time/period';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Label from '@/shared/ui/label';
import { EmptyState, ErrorState, GridSkeleton, ScreenState } from '@/shared/ui/screen-state';
import { SearchFilter } from '@/shared/ui/search-filter';
import * as Select from '@/shared/ui/select';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { exportOfficialPeriod } from '@/shared/api/endpoints/official';
import { ExportButton } from '@/shared/reports/ExportButton';
import { Toolbar, ToolbarField } from '@/shared/ui/toolbar';

import {
  FILTER_OFFICIAL_PERIOD,
  FILTER_ROW_STATE,
  PANEL_IMPORT,
  PANEL_REVIEW,
  PANEL_TRANCHES,
} from '../route';

import { ClosePeriodBar } from './ClosePeriodBar';
import { COLUMN_GROSS, COLUMN_NOTE, COLUMN_PERCENT, buildColumns, validateCell } from './columns';
import {
  useApplyWrits,
  useClosePreview,
  useCreatePeriod,
  useGrid,
  usePeriods,
  useSetRowState,
  useSetRowWrit,
  useSetStates,
  useUpsertRows,
} from './queries';
import { ReviewBar } from './ReviewBar';
import { RowsCaption, TotalsBar } from './TotalsBar';

const VIEW_PERMISSION = 'core.official.view';
const EDIT_PERMISSION = 'core.official.edit';

/** Почему строки на экране не правятся, пока новый период едет: на экране срез прошлого. */
const ПЕРИОД_ЕЩЁ_ЕДЕТ =
  'Период ещё едет: на экране пока строки предыдущего, править и открывать их нельзя. Обычно это доля секунды.';

export function OfficialScreen({ search }: { search: SearchState }) {
  const location = useLocation();
  const navigate = useNavigate();
  const права = useAuthStore((state) => state.permissions);

  /**
   * Когда получены суммы, которые сейчас на экране.
   *
   * `dataUpdatedAt` относится к ТЕКУЩЕМУ ключу запроса, и в момент смены периода он равен нулю —
   * при том что на экране в этот момент намеренно лежат данные прошлого ключа (правило 11,
   * `placeholderData`). Ноль гасил пометку «данные на 12:03» ровно в те 300 мс, ради которых
   * она и существует: под новым заголовком периода висели тринадцать колонок сумм СТАРОГО, и
   * ни одного признака, что это не он. Поэтому помним последнее известное время: оно и есть
   * время того, что человек видит.
   */
  const времяСреза = useRef(0);
  function помнимСрез(текущее: number): Date | null {
    if (текущее !== 0) {
      времяСреза.current = текущее;
    }
    return времяСреза.current === 0 ? null : new Date(времяСреза.current);
  }

  // Право проверяется ДО запросов: без него мы не стучимся в сервер вовсе. Изоляцию держит
  // сервер выборкой, но запрос, про который заранее известно, что он получит отказ, — это
  // лишний круг по сети и лишняя строка в журнале отказов.
  const можноСмотреть = can(права, VIEW_PERMISSION);
  const периоды = usePeriods(undefined, можноСмотреть);
  const список = периоды.data?.items ?? [];
  // Пустое значение в адресе — то же, что его отсутствие: так же его читают панели контура
  // (`panels/context.ts`), и обрезанная ссылка не разводит экран и панель по разным периодам.
  const выбранный = search.filters[FILTER_OFFICIAL_PERIOD] || null;
  // Пусто в адресе — открывается самый свежий период: реестр приходит от новых к старым, и
  // финансовый директор заходит сюда ради текущего, а не ради выбора из списка.
  //
  // Период, НАЗВАННЫЙ в адресе, другим не подменяется никогда. До 20.10.2026 подменялся: только
  // что заведённого периода ещё нет в списке, пока тот едет заново, и экран показывал прошлый
  // период под сообщением «Период 15.10.2026 — 14.11.2026 заведён и открыт». «Загрузить суммы»
  // в это время открывала загрузку в прошлый период, и сентябрьские суммы легли бы в августовский
  // (проверка октябрьского пути их туда и положила). Пока названного нет в списке, экран ждёт
  // ответа сервера, полученного ПОСЛЕ того, как его стали искать: запрос, уже бывший в пути,
  // засчитывается, иначе список спрашивается один раз. Нет и в этом ответе — так и сказано.
  // Сравнение строгое: ответ, пришедший в ту же миллисекунду, в которую период стали искать,
  // мог быть получен ДО этого — тот самый список без него (проверка краснела под нагрузкой).
  // Фоновые обновления списка экран не дёргают: ждём не «что-то едет», а свой ответ.
  const названный = выбранный === null ? null : (список.find((п) => п.id === выбранный) ?? null);
  const период = выбранный === null ? (список[0] ?? null) : названный;
  const periodId = период?.id ?? null;
  const ищемНазванный =
    можноСмотреть && выбранный !== null && названный === null && список.length > 0;
  const [поиск, setПоиск] = useState<{ id: string; с: number } | null>(null);
  if (ищемНазванный && поиск?.id !== выбранный) {
    setПоиск({ id: выбранный, с: Date.now() });
  }
  const свереноССервером =
    поиск !== null && поиск.id === выбранный && периоды.dataUpdatedAt > поиск.с;
  const перезапросить = периоды.refetch;
  useEffect(() => {
    // Ошибку запроса не перезапрашиваем по кругу: её показывает своя плашка с «Повторить».
    if (!ищемНазванный || свереноССервером || периоды.isFetching || периоды.isError) {
      return;
    }
    void перезапросить();
  }, [ищемНазванный, свереноССервером, периоды.isFetching, периоды.isError, перезапросить]);
  const ждёмНазванный = ищемНазванный && !свереноССервером && !периоды.isError;
  const нетНазванного = ищемНазванный && свереноССервером && !периоды.isError;
  const последний = список[0] ?? null;

  const отборСостояния = search.filters[FILTER_ROW_STATE];
  const сетка = useGrid(periodId, { state: отборСостояния, q: search.q }, можноСмотреть);

  const можноПравить = can(права, EDIT_PERMISSION);

  function перейти(следующий: SearchState) {
    void navigate({ pathname: location.pathname, search: buildSearch(следующий) });
  }

  function поставитьОтбор(ключ: string, значение: string | null) {
    const фильтры = { ...search.filters };
    if (значение === null) {
      delete фильтры[ключ];
    } else {
      фильтры[ключ] = значение;
    }
    перейти({ ...search, filters: фильтры, panels: [] });
  }

  /**
   * Снять весь отбор ОДНИМ переходом.
   *
   * Двумя нельзя: второй переход собирается из того же снимка адреса, что и первый, и вернул бы
   * снятое им обратно. Человек нажимал «показать все строки» и получал тот же пустой экран.
   */
  function сброситьОтбор() {
    const фильтры = { ...search.filters };
    delete фильтры[FILTER_ROW_STATE];
    перейти({ ...search, q: undefined, filters: фильтры, panels: [] });
  }

  function открытьПанель(вид: string, id: string) {
    перейти(pushPanel(search, { view: вид, id }));
  }

  if (!можноСмотреть) {
    // Не «доступ запрещён», а кто это видит и что сделать (правило 10). Официальная часть
    // полевым ролям и охраннику не показывается вовсе — это оплаченная строка тз, а не
    // настройка экрана.
    return (
      <main className='flex min-h-0 min-w-0 flex-1 items-center justify-center bg-bg-white-0 p-5'>
        <EmptyState
          icon={RiLockLine}
          title='Официальная часть вам не показывается'
          description='Контур видят финансовый отдел, руководство, операционное руководство и администратор. Остальным он не показывается ни на одном экране, в поиске и в выгрузках.'
          action={
            <span className='text-paragraph-sm text-text-sub-600'>
              Нужен по работе — попросите администратора добавить право.
            </span>
          }
        />
      </main>
    );
  }

  return (
    <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-white-0'>
      <ШапкаПериода
        период={период}
        периоды={список}
        едет={периоды.isPending}
        отказ={периоды.isError}
        можноПравить={можноПравить}
        предпросмотр={periodId}
        строкиДоПравки={сетка.data?.rows ?? []}
        onSelect={(id) => поставитьОтбор(FILTER_OFFICIAL_PERIOD, id)}
        onOpenReview={() => periodId !== null && открытьПанель(PANEL_REVIEW, periodId)}
        onOpenImport={() => periodId !== null && открытьПанель(PANEL_IMPORT, periodId)}
      />

      {периоды.error !== null && (
        <div className='p-5'>
          {/* Ошибка ЗАПРОСА, а не формы: повторять её нечем, кроме перезагрузки страницы, а
              вместе с перезагрузкой уходит вся открытая стопка панелей и незаконченная работа. */}
          <ErrorState
            subject='реестр официальных периодов'
            error={периоды.error as never}
            onRetry={() => void периоды.refetch()}
          />
        </div>
      )}

      {период === null ? (
        периоды.isPending || ждёмНазванный ? (
          <div className='p-5'>
            <GridSkeleton />
          </div>
        ) : нетНазванного ? (
          <div className='flex flex-1 items-center justify-center p-5'>
            <EmptyState
              icon={RiFileList3Line}
              title='Периода по этой ссылке нет в списке'
              description='Ссылка ведёт на официальный период, которого программа не нашла: его не заводили, или ссылку скопировали не целиком. Другой период вместо него сами не открываем, чтобы суммы не легли не туда.'
              action={
                последний !== null && (
                  <Button.Root
                    variant='primary'
                    mode='filled'
                    size='small'
                    onClick={() => поставитьОтбор(FILTER_OFFICIAL_PERIOD, последний.id)}
                  >
                    {`Открыть ${последний.title}`}
                  </Button.Root>
                )
              }
            />
          </div>
        ) : ищемНазванный ? (
          // Список не приехал: об этом говорит плашка ошибки выше, с «Повторить». Сказать
          // здесь ещё и «периода нет» значило бы назвать отказ связи отсутствием периода.
          <></>
        ) : (
          <ПустойРеестр периоды={список} можноПравить={можноПравить} />
        )
      ) : (
        <ScreenState
          data={сетка.data}
          error={сетка.error as never}
          subject='строки официального периода'
          isEmpty={(данные) => данные.rows_total === 0}
          loading={
            <div className='p-5'>
              <GridSkeleton />
            </div>
          }
          empty={
            <div className='flex flex-1 items-center justify-center p-5'>
              {/* Своей кнопки у пустого периода НЕТ: «Загрузить суммы» уже стоит в шапке, и
                  вторая с тем же названием посреди экрана — два входа в одно действие (сквозные
                  проверки «панели» и «кнопки-объясняют» краснели на ней, как только на стенде
                  появлялся пустой период). Пустой период — ровно то, что заказчик видит сразу
                  после «Завести период», поэтому текст называет, где кнопка. */}
              <EmptyState
                icon={RiInboxUnarchiveLine}
                title={`В периоде ${период.title} ещё нет ни одной строки`}
                description={
                  можноПравить && период.can_edit
                    ? 'Нажмите «Загрузить суммы» вверху справа и вставьте фамилии и суммы из книги: программа сопоставит их с людьми и покажет отчёт до записи.'
                    : 'Строки заводятся загрузкой сумм из книги. Суммы загружает финансовый отдел.'
                }
              />
            </div>
          }
          onRetry={() => void сетка.refetch()}
        >
          {(данные) => (
            <Контур
              // Ключ по периоду. Без него `Контур` не размонтируется при смене периода — тот же
              // узел дерева, данные подменяются под ним, — и разобранная вставка переезжает
              // в чужой период: тела собраны из строк старого, а уходят в новый. Это деньги,
              // легшие не туда, и снять их можно только сторно.
              key={данные.period.id}
              данные={данные}
              // Показанные строки принадлежат ЭТОМУ периоду или ещё прошлому: пока новый едет,
              // на экране намеренно лежит прошлый срез (правило 11). Править его нельзя — правка
              // ушла бы в прошлый период под заголовком нового (ревью 20.10.2026).
              актуален={данные.period.id === periodId}
              срез={помнимСрез(сетка.dataUpdatedAt)}
              search={search}
              можноПравить={можноПравить}
              onFilter={поставитьОтбор}
              onSearch={(значение) =>
                перейти({ ...search, q: значение === '' ? undefined : значение, panels: [] })
              }
              onResetFilters={сброситьОтбор}
              onOpenPanel={открытьПанель}
            />
          )}
        </ScreenState>
      )}
    </main>
  );
}

/**
 * Шапка с двумя подписанными календарями (правило 9): сам период с 15 по 15 и связанный с ним
 * месяц ведомости.
 *
 * Месяц ведомости берётся с сервера полями `payroll_year` и `payroll_month`, а не считается
 * здесь. Смещение живёт настройкой `official_period_offset_months`, и посчитанное в браузере
 * однажды молча разойдётся с серверным — ровно в том месте, где спецификация уже чуть не
 * потеряла деньги на сверке Шатуна.
 */
function ШапкаПериода({
  период,
  периоды,
  едет,
  отказ,
  можноПравить,
  предпросмотр,
  строкиДоПравки,
  onSelect,
  onOpenReview,
  onOpenImport,
}: {
  период: OfficialPeriodOut | null;
  периоды: readonly OfficialPeriodOut[];
  едет: boolean;
  /** Список периодов не приехал: пустой выбор — это отказ, а не «периодов нет». */
  отказ: boolean;
  можноПравить: boolean;
  предпросмотр: string | null;
  /** Строки до нажатия «проставить проценты»: по ним считается, что действие изменило. */
  строкиДоПравки: readonly OfficialRowOut[];
  onSelect: (id: string) => void;
  onOpenReview: () => void;
  onOpenImport: () => void;
}) {
  const проценты = useApplyWrits(период?.id ?? 'нет');
  const проценты0 = строкиДоПравки;
  const создание = useCreatePeriod();
  const [сообщение, setСообщение] = useState<string | null>(null);
  const [заводим, setЗаводим] = useState(false);
  const [предложение, setПредложение] = useState<{ year: number; month: number } | null>(null);
  const предпросмотрЗакрытия = useClosePreview(
    предпросмотр,
    период !== null && период.status !== 'closed',
  );

  const кЗаведению = предложение ?? предложитьПериод(периоды);

  return (
    <>
      {/* Шапка держится в одну полосу: экран рабочий, и каждая её лишняя строка — это строка
          сетки, которой финансовый директор не увидит. На ноутбуке 1366×768 разница между
          двухъярусной шапкой и одноярусной — четыре видимых человека из шести. */}
      <Toolbar
        actions={
          период === null ? undefined : (
            <>
              {/* Выгрузка стоит ПЕРВОЙ среди действий над открытым периодом: её жмут чаще
                  всех остальных вместе взятых — заказчик сверяет наш период со своей книгой,
                  а закрывают период раз в месяц. */}
              <ExportButton
                собрать={() => exportOfficialPeriod(период.id, период.title)}
                что='период'
              />
              {/* Порядок и вес кнопок отражают ход работы: сначала загрузить суммы, потом
                  проставить проценты, в конце закрыть период. «Завести период» — действие над
                  списком периодов, а не над открытым, поэтому оно стоит первым и обведено
                  слабее. Раньше все четыре были одного веса, и одна из них вдобавок шла вовсе
                  без рамки: понять, что тут делают обычно, было не по чему. */}
              {можноПравить && (
                <Button.Root
                  variant='neutral'
                  mode='ghost'
                  size='xsmall'
                  onClick={() => setЗаводим((прежнее) => !прежнее)}
                >
                  Завести период
                </Button.Root>
              )}
              {можноПравить && период.can_edit && (
                <>
                  <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={onOpenImport}>
                    Загрузить суммы
                  </Button.Root>
                  <Button.Root
                    variant='neutral'
                    mode='stroke'
                    size='xsmall'
                    onClick={() => {
                      setСообщение(null);
                      // Считается РАЗНИЦА, а не «сколько строк с процентом»: второе — это те, у
                      // кого он и так стоял, и рапорт «проставлено 12» после действия, которое
                      // не сделало ничего, читается как сделанная работа.
                      const было = new Map(проценты0.map((с) => [с.id, с.writ_percent]));
                      проценты.mutate(undefined, {
                        onSuccess: (итог) => {
                          const изменилось = итог.rows.filter(
                            (с) => (было.get(с.id) ?? null) !== с.writ_percent,
                          ).length;
                          const переплаты = итог.rows.filter((с) => с.balance < 0).length;
                          setСообщение(
                            (изменилось === 0
                              ? 'Ничего не изменилось: действующих исполнительных документов нет либо проценты уже стояли.'
                              : `Процент проставлен: строк изменилось ${изменилось}.`) +
                              (переплаты > 0
                                ? ` Строк с переплатой ${переплаты} — на них удержание не встаёт, разберите их вручную.`
                                : ''),
                          );
                        },
                      });
                    }}
                  >
                    {проценты.isPending ? 'Проставляем…' : 'Проставить проценты'}
                  </Button.Root>
                </>
              )}
              <ClosePeriodBar
                период={период}
                // Предпросмотр прошлого периода, оставленный на экране до ответа, — чужие числа
                // под словами «Закрыть период» нового: окно ждёт свой ответ («Считаем…»).
                предпросмотр={
                  предпросмотрЗакрытия.isPlaceholderData ? undefined : предпросмотрЗакрытия.data
                }
                onOpenReview={onOpenReview}
                onGoToPeriod={onSelect}
              />
            </>
          )
        }
      >
        <div className='min-w-64'>
          <SelectField
            id='official-period'
            label='Официальный период'
            value={период?.id ?? NO_VALUE}
            pending={едет}
            failed={отказ}
            options={периоды.map((пункт) => ({
              value: пункт.id,
              // Последний день, а не граница: 15 число принадлежит уже следующему периоду, и в
              // книгах заказчика стоит 14 августа. Подпись собирает сервер.
              label: `${пункт.title} · ${пункт.status_title}`,
            }))}
            onChange={(значение) => значение !== NO_VALUE && onSelect(значение)}
            placeholder='выберите период'
            emptyNote='Официальных периодов ещё нет.'
          />
        </div>

        {период !== null && (
          <ToolbarField label='Связанная ведомость'>
            <span className='truncate'>
              {formatPeriodTitle({ year: период.payroll_year, month: период.payroll_month })}
              <span className='pl-2 text-paragraph-xs font-normal text-text-sub-600'>
                связь задаётся настройкой, а не считается в уме
              </span>
            </span>
          </ToolbarField>
        )}
      </Toolbar>

      {/* Заведение периода — полосой, а не окном: действие обратимое и мелкое, а даты, которые
          получатся, надо показать ДО нажатия. Границы всё равно считает сервер: период,
          ошибочно заведённый на день короче, физически не создать. */}
      {заводим && (
        <div className='flex flex-wrap items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4'>
          <span className='text-paragraph-sm text-text-sub-600'>Заведём период</span>
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            onClick={() => setПредложение(сдвинуть(кЗаведению, -1))}
          >
            ←
          </Button.Root>
          <span className='min-w-56 text-center text-label-sm text-text-strong-950'>
            {подписьПредложения(кЗаведению)}
          </span>
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            onClick={() => setПредложение(сдвинуть(кЗаведению, 1))}
          >
            →
          </Button.Root>
          <Button.Root
            variant='primary'
            mode='filled'
            size='xxsmall'
            onClick={() =>
              создание.mutate(кЗаведению, {
                onSuccess: (новый) => {
                  setЗаводим(false);
                  setПредложение(null);
                  setСообщение(`Период ${новый.title} заведён и открыт.`);
                  onSelect(новый.id);
                },
              })
            }
          >
            {создание.isPending ? 'Заводим…' : 'Завести'}
          </Button.Root>
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            onClick={() => {
              setЗаводим(false);
              setПредложение(null);
            }}
          >
            Отмена
          </Button.Root>
          {создание.error !== null && <ErrorAlert error={создание.error as never} />}
        </div>
      )}

      {сообщение !== null && (
        <p className='border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4 text-paragraph-sm text-text-sub-600'>
          {сообщение}
        </p>
      )}
      {проценты.error !== null && (
        <div className='px-5 py-2 узко:px-4'>
          <ErrorAlert error={проценты.error as never} />
        </div>
      )}
    </>
  );
}

function ПустойРеестр({
  периоды,
  можноПравить,
}: {
  периоды: readonly OfficialPeriodOut[];
  можноПравить: boolean;
}) {
  const создание = useCreatePeriod();
  const предложение = предложитьПериод(периоды);

  return (
    <div className='flex flex-1 items-center justify-center p-5'>
      <EmptyState
        icon={RiFileList3Line}
        title='Официальных периодов ещё нет'
        illustration='обмен'
        description='Период идёт с 15 по 15 число и не совпадает с месяцем ведомости. Заведите первый — границы посчитает программа, ошибиться на день в них невозможно.'
        action={
          можноПравить ? (
            <div className='flex flex-col items-center gap-2'>
              <Button.Root
                variant='primary'
                mode='filled'
                size='small'
                onClick={() => создание.mutate(предложение)}
              >
                {создание.isPending
                  ? 'Заводим…'
                  : `Завести период ${подписьПредложения(предложение)}`}
              </Button.Root>
              {создание.error !== null && <ErrorAlert error={создание.error as never} />}
            </div>
          ) : (
            <span className='text-paragraph-sm text-text-sub-600'>
              Периоды заводит финансовый отдел или администратор.
            </span>
          )
        }
      />
    </div>
  );
}

function Контур({
  данные,
  актуален,
  срез,
  search,
  можноПравить,
  onFilter,
  onSearch,
  onResetFilters,
  onOpenPanel,
}: {
  данные: NonNullable<ReturnType<typeof useGrid>['data']>;
  /** Строки этого периода, а не прошлого, оставленного на экране до ответа. */
  актуален: boolean;
  /** Когда получены показанные числа. Кэш переживает перезагрузку страницы. */
  срез: Date | null;
  search: SearchState;
  можноПравить: boolean;
  onFilter: (ключ: string, значение: string | null) => void;
  onSearch: (значение: string) => void;
  onResetFilters: () => void;
  onOpenPanel: (вид: string, id: string) => void;
}) {
  const строки = useUpsertRows(данные.period.id);
  const процент = useSetRowWrit();
  const состояние = useSetRowState();
  const пачкаСостояний = useSetStates();
  const [выделенные, setВыделенные] = useState<readonly OfficialRowOut[]>([]);
  const [состояниеПачки, setСостояниеПачки] = useState(NO_VALUE);
  const [план, setПлан] = useState<PastePlan<OfficialRowOut> | null>(null);
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [сообщение, setСообщение] = useState<string | null>(null);

  const правится = можноПравить && данные.can_edit && актуален;
  const отборСостояния = search.filters[FILTER_ROW_STATE];

  /** Переход в панель — только по строкам ТОГО ЖЕ периода: чужая строка увела бы в тупик. */
  function открыть(вид: string, id: string) {
    if (!актуален) {
      setЗапрет(ПЕРИОД_ЕЩЁ_ЕДЕТ);
      return;
    }
    onOpenPanel(вид, id);
  }

  // Каталог собирается на каждый рендер намеренно, без мемоизации: он замыкает мутацию смены
  // состояния и переход в панель, а они пересоздаются каждый рендер — мемоизация с ними в
  // зависимостях не срабатывала бы никогда и только притворялась бы оптимизацией. Сборка
  // шестнадцати описаний колонок дешевле одной перерисовки строки.
  const колонки = buildColumns({
    weeks: данные.weeks,
    states: данные.states,
    canEdit: правится,
    blockedReason: правится
      ? null
      : !актуален
        ? ПЕРИОД_ЕЩЁ_ЕДЕТ
        : (данные.blocked_reason ??
          'Вести официальный контур может финансовый отдел или администратор.'),
    showCarried: данные.period.totals.carried_out !== 0,
    onOpenRow: (строка) => открыть(PANEL_TRANCHES, строка.id),
    // Отбор включён — подвал сложит не весь период, и подпись должна сказать об этом словом.
    filtered: отборСостояния !== undefined || (search.q ?? '') !== '',
    onChangeState: (строка, код) => {
      очистить();
      состояние.mutate({ rowId: строка.id, state: код, version: строка.version });
    },
  });

  /**
   * Тело правки строки. Собирается из ТЕКУЩИХ значений и меняет ровно одно поле: загрузка
   * пачкой перезаписывает строку целиком, и не прислав процент, мы сняли бы удержание.
   */
  function телоСтроки(строка: OfficialRowOut, правка: Partial<RowIn>): RowIn {
    return {
      employee_id: строка.employee_id,
      gross_official: строка.gross_official,
      writ_percent: строка.writ_percent,
      state: строка.state,
      note: строка.note,
      ...правка,
    };
  }

  /**
   * Начать новое действие: снять с экрана прошлое сообщение и прошлую причину отказа.
   *
   * Причину отказа надо гасить вместе с сообщением. Иначе «править нельзя, период закрыт»,
   * сказанное однажды, висит над сеткой до перезагрузки страницы и объясняет уже не то, на что
   * человек смотрит.
   */
  function очистить() {
    setСообщение(null);
    setЗапрет(null);
  }

  function сохранить(строка: OfficialRowOut, колонка: string, значение: string) {
    очистить();
    if (колонка === COLUMN_PERCENT) {
      // Отдельный вход, а не загрузка пачкой: он один умеет СНЯТЬ удержание пустым значением и
      // проверяет версию строки. У загрузки пачкой версии нет вовсе (её нет в контракте), то
      // есть правка суммы и заметки чужую правку затрёт молча. Это разрыв контракта, а не
      // экрана: закрывается добавлением версии в тело загрузки.
      процент.mutate({
        rowId: строка.id,
        percent: значение === '' ? null : значение,
        version: строка.version,
      });
      return;
    }
    if (колонка === COLUMN_NOTE) {
      строки.mutate([телоСтроки(строка, { note: значение === '' ? null : значение })]);
      return;
    }
    if (колонка === COLUMN_GROSS) {
      const разбор = parseRublesToKopecks(значение);
      if (!разбор.ok) {
        // Молчаливый выход запрещён так же, как серая кнопка: человек ввёл значение и не
        // получил ничего. Сюда обычно не доходит — сетка проверяет раньше, — но если дойдёт,
        // причина прозвучит.
        setЗапрет(moneyErrorText(разбор.reason, 'полную официальную сумму'));
        return;
      }
      строки.mutate([телоСтроки(строка, { gross_official: разбор.value })]);
    }
  }

  /** Правило 6: «ляжет N, отклонено M и почему» показывается ДО применения, а не после. */
  function применитьВставку() {
    const текущий = план;
    setПлан(null);
    if (текущий === null || текущий.accepted.length === 0) {
      return;
    }
    // Одна строка — одно тело, даже если во вставке её задели трижды: иначе три запроса по
    // одному человеку затрут друг друга, и последний выиграет.
    const поЧеловеку = new Map<string, RowIn>();
    for (const пункт of текущий.accepted) {
      const прежнее = поЧеловеку.get(пункт.row.id) ?? телоСтроки(пункт.row, {});
      if (пункт.columnId === COLUMN_GROSS) {
        const разбор = parseRublesToKopecks(пункт.value);
        if (разбор.ok) {
          прежнее.gross_official = разбор.value;
        }
      }
      if (пункт.columnId === COLUMN_PERCENT) {
        прежнее.writ_percent = пункт.value === '' ? null : пункт.value;
      }
      if (пункт.columnId === COLUMN_NOTE) {
        прежнее.note = пункт.value === '' ? null : пункт.value;
      }
      поЧеловеку.set(пункт.row.id, прежнее);
    }
    строки.mutate([...поЧеловеку.values()], {
      onSuccess: (итог) =>
        setСообщение(
          `Легло строк: ${итог.loaded}.` +
            (итог.skipped.length === 0
              ? ''
              : ` Не легло ${итог.skipped.length}: ${итог.skipped.map((п) => `${п.label} — ${п.reason}`).join('; ')}`),
        ),
    });
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <ReviewBar review={данные.review} onOpen={() => открыть(PANEL_REVIEW, данные.period.id)} />
      <TotalsBar totals={данные.period.totals} />

      <div className='flex flex-wrap items-end gap-3 border-b border-stroke-soft-200 px-5 py-2 узко:px-4'>
        <div className='flex min-w-64 flex-col gap-1'>
          <Label.Root htmlFor='official-query'>Фамилия</Label.Root>
          <SearchFilter
            label='Фамилия'
            placeholder='начните вводить фамилию'
            value={search.q ?? ''}
            onApply={onSearch}
          />
        </div>
        <div className='min-w-56'>
          {/* Сценарий 72: список отбирается по состоянию строки. Отбор идёт на сервере, а не
              фильтром в браузере: счётчик над списком обязан считаться по той же выборке. */}
          <SelectField
            id='official-state'
            label='Состояние'
            value={отборСостояния ?? NO_VALUE}
            options={данные.states.map((пункт) => ({ value: пункт.code, label: пункт.title }))}
            noneLabel='все состояния'
            onChange={(значение) =>
              onFilter(FILTER_ROW_STATE, значение === NO_VALUE ? null : значение)
            }
            placeholder='все состояния'
          />
        </div>
        <div className='ml-auto flex flex-col items-end pb-2'>
          <RowsCaption
            totals={данные.period.totals}
            rowsShown={данные.rows_shown}
            rowsTotal={данные.rows_total}
          />
          {/* Правило 11: прошлый срез показывается сразу и С ПОМЕТКОЙ ВРЕМЕНИ. Кэш переживает
              перезагрузку страницы, и суммы месячной давности без даты неотличимы от свежих. */}
          {срез !== null && (
            <span className='text-paragraph-xs text-text-sub-600'>{formatDataAsOf(срез)}</span>
          )}
        </div>
      </div>

      {данные.blocked_reason !== null && (
        <Alert.Root variant='lighter' status='warning' size='small' className='mx-5 mt-2'>
          <Alert.Icon as={RiLockLine} />
          {данные.blocked_reason}
        </Alert.Root>
      )}
      {запрет !== null && (
        // Пояснение, а не тревога: нажатие на любую нередактируемую ячейку — обычное дело, и
        // жёлтая полоса на каждый такой клик кричит там, где ничего не случилось. Закрытый
        // период при этом остаётся отдельным предупреждением выше. Полосу можно снять — иначе
        // сказанное однажды висит до перезагрузки страницы.
        <div className='mx-5 mt-2'>
          <Alert.Root variant='lighter' status='information' size='small'>
            <Alert.Icon as={RiInformationLine} />
            <span className='flex-1'>{запрет}</span>
            <button
              type='button'
              aria-label='Убрать пояснение'
              onClick={() => setЗапрет(null)}
              className='text-text-sub-600 hover:text-text-strong-950'
            >
              <RiCloseLine className='size-4' />
            </button>
          </Alert.Root>
        </div>
      )}
      {сообщение !== null && (
        <p className='border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4 text-paragraph-sm text-text-sub-600'>
          {сообщение}
        </p>
      )}
      {[строки.error, процент.error, состояние.error, пачкаСостояний.error]
        .filter((ошибка) => ошибка !== null)
        .map((ошибка, номер) => (
          <div key={номер} className='px-5 py-2 узко:px-4'>
            <ErrorAlert error={ошибка as never} />
          </div>
        ))}

      {план !== null && (
        <div className='flex flex-wrap items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4'>
          <span className='text-paragraph-sm text-text-sub-600'>{план.summary}</span>
          <Button.Root
            variant='primary'
            mode='filled'
            size='xxsmall'
            // Кнопка не сереет и здесь: выключенная не доводит нажатие до обработчика, и
            // человек, вставивший не тот кусок, не получает вообще ничего в ответ.
            onClick={
              план.accepted.length === 0
                ? () =>
                    setЗапрет(
                      'Из вставленного не ложится ничего. Причины перечислены слева: поправьте кусок в книге и вставьте заново.',
                    )
                : применитьВставку
            }
          >
            Применить
          </Button.Root>
          <Button.Root variant='neutral' mode='stroke' size='xxsmall' onClick={() => setПлан(null)}>
            Отменить
          </Button.Root>
        </div>
      )}

      <div className='min-h-0 flex-1'>
        {данные.rows.length === 0 ? (
          // Отбор ничего не нашёл — это НЕ «строк нет»: разводится по `rows_shown` и
          // `rows_total`, которые сервер шлёт рядом именно ради этой развилки (правило 7).
          <div className='flex h-full items-center justify-center p-5'>
            <EmptyState
              title='По этому отбору строк нет'
              description={`В периоде ${countLabel(данные.rows_total, ['строка', 'строки', 'строк'])}, но ни одна не подходит под отбор.`}
              action={
                <Button.Root variant='neutral' mode='stroke' size='small' onClick={onResetFilters}>
                  Показать все строки
                </Button.Root>
              }
            />
          </div>
        ) : (
          <DataGrid<OfficialRowOut>
            rows={данные.rows}
            columns={колонки}
            rowKey={(строка) => строка.id}
            rowLabel={(строка) => строка.employee_name}
            onCommit={правится ? сохранить : undefined}
            onPaste={
              правится
                ? (разбор) => {
                    очистить();
                    setПлан(разбор);
                  }
                : undefined
            }
            onBlocked={setЗапрет}
            onSelectionChange={setВыделенные}
            validate={validateCell}
            emptyText='В периоде нет ни одной строки: суммы по людям ещё не загружены. Нажмите «Загрузить суммы» и вставьте кусок из книги заказчика.'
          />
        )}
      </div>

      {/* Действие над выделенным живёт ВНИЗУ, а не в шапке: в шапке оно двигало бы сетку вниз
          при каждом протягивании выделения, и следующее нажатие попадало бы не в ту строку
          (то же правило, что и у панели выделения в реестре людей).

          Тз, раздел 5.4.5, называет массовую отметку прямо. До 26.08.2026 состояние менялось
          только по одной строке: восемьдесят человек «можно выдавать» это восемьдесят нажатий
          на раскрывающийся список. Дверь на сервере при этом была и писала журнал поимённо. */}
      {правится && выделенные.length > 1 && (
        <div className='flex flex-wrap items-center gap-3 border-t border-stroke-soft-200 bg-bg-weak-50 px-5 py-2 узко:px-4'>
          <span className='text-paragraph-sm text-text-sub-600'>
            Выделено {countLabel(выделенные.length, ['строка', 'строки', 'строк'])} · присвоить
            состояние
          </span>
          {/* Выбор берётся из дизайн-системы, а не набирается своей вёрсткой: своя рамка и
              своё скругление рядом с общими полями читаются как чужой элемент. Подписи сверху
              здесь нет намеренно — в служебной полосе она добавила бы вторую строку и подняла
              полосу над сеткой. */}
          {/* Ширина задана: выбор дизайн-системы по умолчанию тянется во всю строку, и полоса
              вырастала в три ряда, съедая строки сетки. */}
          <div className='w-64'>
            <Select.Root
              size='xsmall'
              value={состояниеПачки === NO_VALUE ? undefined : состояниеПачки}
              onValueChange={setСостояниеПачки}
            >
              <Select.Trigger id='official-bulk-state' aria-label='Состояние для выделенных строк'>
                <Select.Value placeholder='выберите состояние' />
              </Select.Trigger>
              <Select.Content>
                {данные.states.map((пункт) => (
                  <Select.Item key={пункт.code} value={пункт.code}>
                    {пункт.title}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>
          <Button.Root
            variant='primary'
            mode='filled'
            size='xxsmall'
            // Кнопка не сереет: выключенная не доводит нажатие до обработчика, и человек,
            // не выбравший состояние, не получает вообще ничего в ответ.
            onClick={() => {
              if (состояниеПачки === NO_VALUE) {
                setЗапрет('Выберите состояние: без него присваивать нечего.');
                return;
              }
              очистить();
              пачкаСостояний.mutate(
                {
                  periodId: данные.period.id,
                  rowIds: выделенные.map((строка) => строка.id),
                  state: состояниеПачки,
                },
                {
                  onSuccess: (итог) => {
                    const название =
                      данные.states.find((пункт) => пункт.code === состояниеПачки)?.title ??
                      состояниеПачки;
                    // Пропущенные проговариваются, а не проглатываются: человек, выделивший
                    // восемьдесят строк, обязан знать, скольких из них действие не коснулось.
                    setСообщение(
                      итог.skipped.length === 0
                        ? `Состояние «${название}» присвоено ${countLabel(итог.changed, ['строке', 'строкам', 'строкам'])}.`
                        : `Состояние «${название}» присвоено ${countLabel(итог.changed, ['строке', 'строкам', 'строкам'])}. Не тронуто ${итог.skipped.length}: ${итог.skipped
                            .map((пропуск) => `${пропуск.label} — ${пропуск.reason}`)
                            .join('; ')}.`,
                    );
                  },
                },
              );
            }}
          >
            {пачкаСостояний.isPending ? 'Присваиваем…' : 'Присвоить'}
          </Button.Root>
        </div>
      )}
    </div>
  );
}

/**
 * Какой период предложить завести.
 *
 * От самого свежего существующего — следующий за ним; реестр приходит от новых к старым.
 * Реестр пуст — текущий, если сегодня уже 15 число или позже, иначе прошлый: до 15 числа идёт
 * период, начавшийся месяцем раньше.
 */
export function предложитьПериод(
  периоды: readonly OfficialPeriodOut[],
  сегодня: Date = new Date(),
): { year: number; month: number } {
  const свежий = периоды[0];
  if (свежий !== undefined) {
    // Дата разбирается текстом, а не через Date: западнее Гринвича разбор датой съезжает на день.
    const год = Number(свежий.date_from.slice(0, 4));
    const месяц = Number(свежий.date_from.slice(5, 7));
    return месяц === 12 ? { year: год + 1, month: 1 } : { year: год, month: месяц + 1 };
  }
  const год = сегодня.getFullYear();
  const месяц = сегодня.getMonth() + 1;
  if (сегодня.getDate() >= 15) {
    return { year: год, month: месяц };
  }
  return месяц === 1 ? { year: год - 1, month: 12 } : { year: год, month: месяц - 1 };
}

/** Сдвиг предложения на месяц вперёд или назад. */
export function сдвинуть(
  { year, month }: { year: number; month: number },
  месяцев: number,
): { year: number; month: number } {
  const всего = year * 12 + (month - 1) + месяцев;
  return { year: Math.floor(всего / 12), month: (всего % 12) + 1 };
}

/** Подпись предложения: период всегда идёт с 15 числа по 14 число следующего месяца. */
export function подписьПредложения({ year, month }: { year: number; month: number }): string {
  const конецМесяца = month === 12 ? 1 : month + 1;
  const конецГода = month === 12 ? year + 1 : year;
  return `15.${двумя(month)}.${year} — 14.${двумя(конецМесяца)}.${конецГода}`;
}

function двумя(число: number): string {
  return String(число).padStart(2, '0');
}
